import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, CalendarPlus, Clock, AlertTriangle } from 'lucide-react';
import { getAvailableSlots } from '../../services/daysmartService';

const DURATION_OPTIONS = [
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
    { value: 150, label: '2.5 hours' },
    { value: 180, label: '3 hours' },
    { value: 210, label: '3.5 hours' },
    { value: 240, label: '4 hours' },
];

const MIN_DAYS_WITH_SLOTS = 4;
const MAX_DAYS_TO_SEARCH = 90;

const GroomerAvailabilityModal = ({ isOpen, onClose, employee, locationId }) => {
    const [selectedDate, setSelectedDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    });
    const [duration, setDuration] = useState(120);
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchingDate, setSearchingDate] = useState(null);
    const [daysFound, setDaysFound] = useState(0);

    // Drag state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const dragStartRef = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);

    const locationName = locationId === 1 ? 'Hopkins' : 'Inver Grove';

    const handleSearch = useCallback(async () => {
        if (!selectedDate || !employee) return;

        setLoading(true);
        setError(null);
        setHasSearched(true);
        setSlots([]);

        try {
            const allSlots = [];
            const daysWithSlots = new Set();
            let currentDate = new Date(selectedDate + 'T12:00:00'); // noon to avoid TZ issues

            for (let i = 0; i < MAX_DAYS_TO_SEARCH && daysWithSlots.size < MIN_DAYS_WITH_SLOTS; i++) {
                const dateStr = currentDate.toISOString().split('T')[0];
                setSearchingDate(dateStr);
                setDaysFound(daysWithSlots.size);

                const result = await getAvailableSlots(
                    locationId,
                    dateStr,
                    employee.employee_id,
                    employee.name,
                    duration,
                    true // exact_date - only check this specific day
                );

                const daySlots = (result.available_slots || []).filter(s => {
                    // Only keep slots for the exact date queried (backend returns ±2 days)
                    const slotDate = s.date || (s.start_time ? s.start_time.slice(0, 10) : '');
                    if (slotDate !== dateStr) return false;
                    // Only keep slots for the exact employee requested
                    return !s.employee_id || s.employee_id === employee.employee_id;
                });

                if (daySlots.length > 0) {
                    // Tag each slot with the date we searched
                    daySlots.forEach(s => { s._searchDate = dateStr; });
                    allSlots.push(...daySlots);
                    daysWithSlots.add(dateStr);
                }

                currentDate.setDate(currentDate.getDate() + 1);
            }

            setSlots(allSlots);
            setSearchingDate(null);
        } catch (err) {
            setError(err.message);
            setSlots([]);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, employee, locationId, duration]);

    // Auto-search when modal opens or params change
    useEffect(() => {
        if (isOpen && employee) {
            handleSearch();
        }
    }, [isOpen, employee, handleSearch]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSlots([]);
            setError(null);
            setHasSearched(false);
            setSearchingDate(null);
            setDaysFound(0);
            setPosition({ x: 0, y: 0 });
        }
    }, [isOpen]);

    // Drag handlers
    const handleMouseDown = (e) => {
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
        setIsDragging(true);
        dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    };

    useEffect(() => {
        if (!isDragging) return;
        const handleMouseMove = (e) => {
            setPosition({
                x: e.clientX - dragStartRef.current.x,
                y: e.clientY - dragStartRef.current.y,
            });
        };
        const handleMouseUp = () => setIsDragging(false);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    if (!isOpen || !employee) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div
                ref={modalRef}
                className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
                style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
            >
                {/* Header */}
                <div
                    className="bg-purple-600 text-white px-6 py-4 flex items-center justify-between cursor-grab"
                    onMouseDown={handleMouseDown}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <div>
                        <h2 className="text-xl font-semibold">{employee.name}</h2>
                        <p className="text-purple-200 text-sm">Availability • {locationName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-purple-700 rounded-full p-1 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-4">
                        {/* Search Controls */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <CalendarPlus className="text-green-600" size={22} />
                                <h3 className="font-semibold text-green-800 text-lg">Check Availability</h3>
                            </div>

                            <div className="flex gap-3 items-end">
                                <div className="flex-1">
                                    <label className="text-xs font-medium text-green-700 block mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-green-700 block mb-1">Duration</label>
                                    <select
                                        value={duration}
                                        onChange={(e) => setDuration(parseInt(e.target.value))}
                                        className="px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-green-500 focus:border-green-500"
                                    >
                                        {DURATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={handleSearch}
                                    disabled={loading}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={16} /> : 'Search'}
                                </button>
                            </div>
                        </div>

                        {/* Results */}
                        {loading && (
                            <div className="flex items-center gap-2 text-green-600 py-4 justify-center">
                                <Loader2 className="animate-spin" size={18} />
                                <span className="text-sm">
                                    Checking {searchingDate ? new Date(searchingDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '...'}  
                                    <span className="text-green-400 text-xs ml-1">({daysFound}/{MIN_DAYS_WITH_SLOTS} days found)</span>
                                </span>
                            </div>
                        )}

                        {error && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-yellow-700">
                                    <AlertTriangle size={16} />
                                    <span className="text-sm">{error}</span>
                                </div>
                            </div>
                        )}

                        {!loading && hasSearched && slots.length === 0 && !error && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                                <p className="text-sm text-gray-500">No available slots found for this date range.</p>
                                <p className="text-xs text-gray-400 mt-1">Try a different date or shorter duration.</p>
                            </div>
                        )}

                        {!loading && slots.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-xs font-medium text-green-700 mb-2">
                                    Available Slots ({slots.length}):
                                </p>
                                <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                                    {slots.map((slot, idx) => {
                                        const startDate = new Date(slot.start_time);
                                        const endDate = new Date(slot.end_time);
                                        const durationMs = endDate - startDate;
                                        const durationHours = Math.floor(durationMs / 3600000);
                                        const durationMins = Math.floor((durationMs % 3600000) / 60000);
                                        const durationStr = durationHours > 0 ? `${durationHours}h ${durationMins}m` : `${durationMins}m`;

                                        return (
                                            <div
                                                key={idx}
                                                className="text-xs p-2 rounded border bg-white border-green-200 hover:bg-green-50 transition-colors"
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-green-800">
                                                        {startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                    </span>
                                                    <span className="text-green-600">
                                                        {startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                        {' - '}
                                                        {endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center mt-1">
                                                    <span className="text-xs text-gray-500">
                                                        with {slot.employee_name || employee.name}
                                                    </span>
                                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                                        <Clock size={10} />
                                                        {durationStr}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t px-6 py-4 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full py-2 px-4 rounded-lg font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GroomerAvailabilityModal;
